import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Smartphone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useLang } from "../../i18n/LangContext";
import { useSite } from "../../lib/SiteContext";
import { Reveal, Overline } from "./Reveal";

const EMPTY = { name: "", mobile: "", pincode: "", state: "", business: "", role: "retailer", otp: "" };

const Field = ({ label, testid, ...p }) => (
  <label className="block">
    <span className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</span>
    <input data-testid={testid} className="field mt-2" {...p} />
  </label>
);

export const JoinForm = () => {
  const { t, lang } = useLang();
  const { settings } = useSite();
  const j = t.join;
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [otpInfo, setOtpInfo] = useState(null);
  const [otpBusy, setOtpBusy] = useState(false);
  const otpOn = settings.otp_required !== false;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const sendOtp = async () => {
    setOtpBusy(true);
    try {
      const { data } = await api.post("/otp/send", { mobile: f.mobile });
      setOtpInfo({ mobile: f.mobile, code: data?.demo_code ?? "" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setOtpBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (otpOn && (!otpInfo || otpInfo.mobile !== f.mobile || f.otp.length !== 6)) {
      toast.error(j.needOtp);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/leads", { ...f, language: lang });
      setDone(data?.ref ?? "");
      setF(EMPTY);
      setOtpInfo(null);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="join" className="py-24 lg:py-32" data-testid="join-section">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-12 lg:px-8">
        <Reveal className="lg:col-span-5">
          <Overline>{j.over}</Overline>
          <h2 className="section-title mt-5">{j.title}</h2>
          <p className="mt-5 text-slate-300">{j.sub}</p>
          <div className="mt-10 flex items-center gap-5 rounded-[24px] border border-white/10 bg-card p-6" data-testid="app-download-cta">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-emerald/15 text-emerald"><Smartphone size={26} /></span>
            <div>
              <p className="font-display text-lg font-semibold text-white">{j.app}</p>
              <p className="text-sm text-slate-300">{j.appSub}</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <div className="rounded-[32px] border border-white/10 bg-card p-7 sm:p-10">
            {done !== null ? (
              <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="py-10 text-center" data-testid="lead-success">
                <CheckCircle2 size={56} className="mx-auto text-emerald" />
                <h3 className="mt-6 font-display text-3xl font-bold text-white">{j.done}</h3>
                <p className="mt-4 text-sm text-slate-400">{j.ref}</p>
                <p className="mt-1 font-mono text-3xl font-semibold text-saffron" data-testid="lead-ref-id">{done}</p>
                <p className="mt-4 text-slate-300">{j.doneSub}</p>
                <button className="btn-ghost mt-8" onClick={() => setDone(null)} data-testid="lead-again-btn">{j.again}</button>
              </motion.div>
            ) : !settings.accepting_leads ? (
              <p className="py-16 text-center font-display text-2xl text-white" data-testid="leads-paused">{j.paused}</p>
            ) : (
              <form onSubmit={submit} className="grid gap-6 sm:grid-cols-2" data-testid="join-lead-form">
                <div className="sm:col-span-2"><Field label={j.name} testid="lead-name-input" required minLength={2} value={f.name} onChange={set("name")} /></div>
                <div>
                  <Field label={j.mobile} testid="lead-mobile-input" required inputMode="numeric" maxLength={10} pattern="[6-9][0-9]{9}" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value.replace(/\D/g, "") })} />
                  {otpOn && (
                    <button type="button" disabled={otpBusy || !/^[6-9]\d{9}$/.test(f.mobile)} onClick={sendOtp} data-testid="lead-send-otp-btn"
                      className="mt-3 inline-flex items-center gap-2 rounded-full border border-saffron/50 px-4 py-1.5 text-xs font-semibold text-orange-300 transition-colors duration-300 hover:bg-saffron/10 disabled:cursor-not-allowed disabled:opacity-40">
                      {otpBusy && <Loader2 size={12} className="animate-spin" />}
                      {otpInfo?.mobile === f.mobile ? j.resendOtp : j.sendOtp}
                    </button>
                  )}
                </div>
                <Field label={j.pincode} testid="lead-pincode-input" required inputMode="numeric" maxLength={6} pattern="[0-9]{6}" value={f.pincode} onChange={(e) => setF({ ...f, pincode: e.target.value.replace(/\D/g, "") })} />
                {otpOn && otpInfo?.mobile === f.mobile && (
                  <div className="rounded-2xl border border-emerald/30 bg-emerald/10 p-4 sm:col-span-2" data-testid="otp-box">
                    <p className="text-xs text-emerald-300">{j.otpSent} +91 {otpInfo.mobile}</p>
                    {otpInfo.code && (
                      <p className="mt-1 text-sm text-slate-200" data-testid="otp-demo-code">{j.demoOtp} <span className="keep-mono font-mono text-lg font-semibold tracking-[0.3em] text-white">{otpInfo.code}</span></p>
                    )}
                    <input data-testid="lead-otp-input" className="field keep-mono mt-2 font-mono text-xl tracking-[0.5em] placeholder:font-sans placeholder:text-sm placeholder:tracking-normal" inputMode="numeric" maxLength={6} placeholder={j.otp}
                      value={f.otp} onChange={(e) => setF({ ...f, otp: e.target.value.replace(/\D/g, "") })} />
                  </div>
                )}
                <Field label={j.state} testid="lead-state-input" required minLength={2} value={f.state} onChange={set("state")} />
                <Field label={j.business} testid="lead-business-input" value={f.business} onChange={set("business")} />
                <div className="sm:col-span-2">
                  <span className="text-xs font-medium uppercase tracking-wider text-slate-400">{j.role}</span>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {["retailer", "distributor", "shg"].map((r) => (
                      <button type="button" key={r} onClick={() => setF({ ...f, role: r })} data-testid={`lead-role-${r}`}
                        className={`rounded-xl border px-2 py-3 text-xs font-semibold transition-colors duration-300 sm:text-sm ${f.role === r ? "border-saffron bg-saffron/15 text-white" : "border-white/10 text-slate-300 hover:border-white/30"}`}>
                        {t.solutions.tabs[r]}
                      </button>
                    ))}
                  </div>
                </div>
                <button type="submit" disabled={busy} className="btn-primary btn-lg justify-center sm:col-span-2" data-testid="lead-submit-btn">
                  {busy ? <><Loader2 className="animate-spin" size={18} /> {j.sending}</> : j.submit}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
